<template>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <div class="d-flex align-center mb-4">
            <h1 class="text-h4">Evaluation Templates</h1>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="openCreateDialog">
              <v-icon left>mdi-plus</v-icon>
              Create Template
            </v-btn>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="d-flex justify-center align-center py-5">
            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          </div>

          <!-- Error State -->
          <v-alert v-if="error" type="error" class="mb-4">
            {{ error }}
          </v-alert>

          <!-- Templates List -->
          <v-card v-if="!loading">
            <v-data-table
              :headers="headers"
              :items="templates"
              :search="search"
              :items-per-page="10"
            >
              <template #top>
<!--                <v-toolbar flat>-->
<!--                  <v-text-field-->
<!--                    v-model="search"-->
<!--                    append-icon="mdi-magnify"-->
<!--                    label="Search templates"-->
<!--                    single-line-->
<!--                    hide-details-->
<!--                    class="mx-4"-->
<!--                  ></v-text-field>-->
<!--                </v-toolbar>-->
              </template>

              <template v-slot:[`item.active`]="{ item }">
                <v-chip
                  :color="item.active ? 'success' : 'grey'"
                  small
                >
                  {{ item.active ? 'Active' : 'Inactive' }}
                </v-chip>
              </template>

              <template v-slot:[`item.createdAt`]="{ item }">
                {{ formatDate(item.createdAt) }}
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      class="mr-2"
                      v-bind="attrs"
                      v-on="on"
                      @click="viewTemplate(item)"
                    >
                      <v-icon>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>View Template</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      class="mr-2"
                      v-bind="attrs"
                      v-on="on"
                      @click="editTemplate(item)"
                    >
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>Edit Template</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      class="mr-2"
                      v-bind="attrs"
                      v-on="on"
                      @click="cloneTemplate(item)"
                    >
                      <v-icon>mdi-content-copy</v-icon>
                    </v-btn>
                  </template>
                  <span>Clone Template</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      class="mr-2"
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      @click="openDocumentsDialog(item)"
                    >
                      <v-icon>mdi-file-document-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>Document Templates</span>
                </v-tooltip>

                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="error"
                      v-bind="attrs"
                      v-on="on"
                      @click="confirmDelete(item)"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete Template</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>

      <!-- Create/Edit Dialog -->
      <v-dialog v-model="dialog" max-width="600px">
        <v-card>
          <v-card-title>
            <span class="text-h5">{{ editMode ? 'Edit Template' : 'Create Template' }}</span>
          </v-card-title>

          <v-card-text>
            <v-form ref="form" v-model="valid">
              <v-text-field
                v-model="editedItem.name"
                label="Template Name"
                :rules="[v => !!v || 'Name is required']"
                required
              ></v-text-field>

              <v-textarea
                v-model="editedItem.description"
                label="Description"
                rows="3"
              ></v-textarea>

              <v-text-field
                v-model="editedItem.version"
                label="Version"
                :rules="[v => !!v || 'Version is required']"
                required
              ></v-text-field>

              <v-switch
                v-model="editedItem.active"
                label="Active"
                color="primary"
              ></v-switch>
            </v-form>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="closeDialog">
              Cancel
            </v-btn>
            <v-btn color="primary" text @click="saveTemplate" :loading="saving">
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="deleteDialog" max-width="400px">
        <v-card>
          <v-card-title class="text-h5">Delete Template</v-card-title>
          <v-card-text>
            Are you sure you want to delete this template?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="deleteDialog = false">Cancel</v-btn>
            <v-btn color="error" text @click="deleteTemplate" :loading="deleting">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Clone Dialog -->
      <v-dialog v-model="cloneDialog" max-width="500px">
        <v-card>
          <v-card-title class="text-h5">Clone Template</v-card-title>
          <v-card-text>
            <v-form ref="cloneForm" v-model="cloneFormValid">
              <v-text-field
                v-model="newVersion"
                label="New Version"
                :rules="[v => !!v || 'Version is required']"
                required
              ></v-text-field>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="closeCloneDialog">Cancel</v-btn>
            <v-btn 
              color="primary" 
              text 
              @click="confirmClone" 
              :loading="cloning"
              :disabled="!cloneFormValid"
            >
              Clone
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Document Templates Dialog -->
      <v-dialog v-model="documentsDialog" max-width="700px">
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon left color="primary">mdi-file-document-outline</v-icon>
            <span class="text-h5">Document Templates</span>
            <v-spacer></v-spacer>
            <v-btn icon @click="documentsDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pt-4">
            <div v-if="selectedTemplateForDocs" class="mb-4">
              <v-chip color="primary" outlined small>
                {{ selectedTemplateForDocs.name }} v{{ selectedTemplateForDocs.version }}
              </v-chip>
            </div>

            <!-- Upload Section -->
            <v-card outlined class="mb-4 pa-4">
              <div class="text-subtitle-2 mb-3">Upload New Document Template</div>
              <v-form ref="uploadForm" v-model="uploadFormValid">
                <v-text-field
                  v-model="newDocumentName"
                  label="Document Name"
                  :rules="[v => !!v || 'Name is required']"
                  outlined
                  dense
                  class="mb-2"
                ></v-text-field>
                <v-file-input
                  v-model="documentFile"
                  label="Word Template (.docx)"
                  accept=".docx"
                  :rules="[v => !!v || 'File is required']"
                  outlined
                  dense
                  prepend-icon="mdi-file-word"
                  show-size
                ></v-file-input>
                <div class="text-caption grey--text mb-3">
                  Use <code v-pre>{{fieldCode}}</code> placeholders in your Word document. They will be replaced with evaluation responses.
                </div>
                <v-btn
                  color="primary"
                  :loading="uploadingDocument"
                  :disabled="!uploadFormValid"
                  @click="uploadDocument"
                >
                  <v-icon left>mdi-upload</v-icon>
                  Upload
                </v-btn>
              </v-form>
            </v-card>

            <!-- Documents List -->
            <div class="text-subtitle-2 mb-3">Existing Documents</div>
            <v-progress-linear v-if="loadingDocuments" indeterminate color="primary"></v-progress-linear>
            <v-list v-else-if="templateDocuments.length > 0" dense>
              <v-list-item v-for="doc in templateDocuments" :key="doc.id">
                <v-list-item-icon>
                  <v-icon color="primary">mdi-file-pdf-box</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title>{{ doc.documentName }}</v-list-item-title>
                  <v-list-item-subtitle>
                    Created: {{ formatDate(doc.creationTime) }}
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action>
                  <v-btn
                    icon
                    small
                    color="error"
                    @click="confirmDeleteDocument(doc)"
                    :loading="deletingDocumentId === doc.id"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
            <v-alert v-else type="info" text dense>
              No document templates uploaded yet.
            </v-alert>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Delete Document Confirmation -->
      <v-dialog v-model="deleteDocumentDialog" max-width="400px">
        <v-card>
          <v-card-title class="text-h6">Delete Document Template</v-card-title>
          <v-card-text>
            Are you sure you want to delete "{{ documentToDelete?.documentName }}"?
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="deleteDocumentDialog = false">Cancel</v-btn>
            <v-btn color="error" text @click="deleteDocument" :loading="deletingDocument">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
</template>

<script>
import {
  getTemplatesApi,
  createTemplateApi,
  updateTemplateApi,
  deleteTemplateApi,
  cloneTemplateApi
} from '@/shared/apis/aper/aperTemplate';
import {
  getDocumentsByTemplate,
  createEvaluationDocument,
  deleteEvaluationDocument
} from '@/shared/apis/aper/aperDocuments';
export default {
  name: 'TemplatesView',
  components: {

  },
  data() {
    return {
      templates: [],
      loading: false,
      error: null,
      search: '',
      dialog: false,
      deleteDialog: false,
      cloneDialog: false,
      editMode: false,
      saving: false,
      deleting: false,
      cloning: false,
      valid: true,
      cloneFormValid: true,
      newVersion: '',
      selectedTemplate: null,
      editedItem: {
        name: '',
        version: '',
        active: true,
        sections: []
      },
      headers: [
        { text: 'Name', value: 'name' },
        { text: 'Version', value: 'version' },
        { text: 'Status', value: 'active' },
        { text: 'Created', value: 'createdAt' },
        { text: 'Actions', value: 'actions', sortable: false }
      ],
      documentsDialog: false,
      selectedTemplateForDocs: null,
      templateDocuments: [],
      loadingDocuments: false,
      uploadFormValid: false,
      newDocumentName: '',
      documentFile: null,
      uploadingDocument: false,
      deleteDocumentDialog: false,
      documentToDelete: null,
      deletingDocument: false,
      deletingDocumentId: null
    }
  },
  async created() {
    await this.fetchTemplates();
  },
  methods: {
    async fetchTemplates() {
      this.loading = true;
      this.error = null;
      try {
        const response = await getTemplatesApi();
        this.templates = response.data;
      } catch (error) {
        console.log(error)
        this.error = 'Failed to load templates';
        console.error('Error fetching templates:', error);
      } finally {
        this.loading = false;
      }
    },
    openCreateDialog() {
      this.editMode = false;
      this.editedItem = {
        name: '',
        version: '',
        active: true,
        sections: []
      };
      this.dialog = true;
    },
    editTemplate(template) {
      this.editMode = true;
      this.editedItem = { ...template };
      this.dialog = true;
    },
    async saveTemplate() {
      if (!this.$refs.form.validate()) return;

      this.saving = true;
      try {
        const payload = { ...this.editedItem };

        if (this.editMode) {
          await updateTemplateApi(payload.id, payload);
        } else {
          await createTemplateApi(payload);
        }
        await this.fetchTemplates();
        this.closeDialog();
      } catch (error) {
        console.error('Error saving template:', error);
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(template) {
      this.selectedTemplate = template;
      this.deleteDialog = true;
    },
    async deleteTemplate() {
      if (!this.selectedTemplate) return;

      this.deleting = true;
      try {
        await deleteTemplateApi(this.selectedTemplate.id);
        await this.fetchTemplates();
        this.deleteDialog = false;
      } catch (error) {
        console.error('Error deleting template:', error);
      } finally {
        this.deleting = false;
      }
    },
    cloneTemplate(template) {
      this.selectedTemplate = template;
      this.newVersion = '';
      this.cloneDialog = true;
    },
    async confirmClone() {
      if (!this.$refs.cloneForm.validate()) return;
      if (!this.selectedTemplate || !this.newVersion) return;

      this.cloning = true;
      try {
        await cloneTemplateApi(this.selectedTemplate.id, this.newVersion);
        await this.fetchTemplates();
        this.closeCloneDialog();
      } catch (error) {
        console.error('Error cloning template:', error);
      } finally {
        this.cloning = false;
      }
    },
    closeCloneDialog() {
      this.cloneDialog = false;
      this.$nextTick(() => {
        if (this.$refs.cloneForm) this.$refs.cloneForm.reset();
        this.newVersion = '';
        this.selectedTemplate = null;
      });
    },
    closeDialog() {
      this.dialog = false;
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.reset();
        this.editedItem = {
          name: '',
          version: '',
          active: true,
          sections: []
        };
      });
    },
    viewTemplate(template) {
      this.$router.push(`/evaluation/templates/${template.id}`);
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString();
    },
    async openDocumentsDialog(template) {
      this.selectedTemplateForDocs = template;
      this.documentsDialog = true;
      this.newDocumentName = '';
      this.documentFile = null;
      await this.fetchTemplateDocuments();
    },
    async fetchTemplateDocuments() {
      if (!this.selectedTemplateForDocs) return;
      this.loadingDocuments = true;
      try {
        const response = await getDocumentsByTemplate(this.selectedTemplateForDocs.id);
        this.templateDocuments = response.data || [];
      } catch (error) {
        console.error('Error fetching documents:', error);
        this.templateDocuments = [];
      } finally {
        this.loadingDocuments = false;
      }
    },
    async uploadDocument() {
      if (!this.$refs.uploadForm.validate()) return;
      if (!this.documentFile || !this.newDocumentName || !this.selectedTemplateForDocs) return;

      this.uploadingDocument = true;
      try {
        await createEvaluationDocument(
          this.documentFile,
          this.newDocumentName,
          this.selectedTemplateForDocs.id
        );
        this.newDocumentName = '';
        this.documentFile = null;
        this.$refs.uploadForm.reset();
        await this.fetchTemplateDocuments();
      } catch (error) {
        console.error('Error uploading document:', error);
      } finally {
        this.uploadingDocument = false;
      }
    },
    confirmDeleteDocument(doc) {
      this.documentToDelete = doc;
      this.deleteDocumentDialog = true;
    },
    async deleteDocument() {
      if (!this.documentToDelete) return;

      this.deletingDocument = true;
      this.deletingDocumentId = this.documentToDelete.id;
      try {
        await deleteEvaluationDocument(this.documentToDelete.id);
        this.deleteDocumentDialog = false;
        this.documentToDelete = null;
        await this.fetchTemplateDocuments();
      } catch (error) {
        console.error('Error deleting document:', error);
      } finally {
        this.deletingDocument = false;
        this.deletingDocumentId = null;
      }
    }
  }
}
</script>
